import { Injectable, NgZone, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '@environments/environment';
import { User } from '@domain/models/account-settings/user.model';
import { AnalyticsEventParams, AnalyticsGateway } from '@domain/models/analytics/gateway/analytics.gateway';
import { resolveGa4Brand } from '@infraestructure/helpers/analytics/ga4-brand.helper';
import { isGa4AllowedHost } from '@infraestructure/helpers/analytics/ga4-host.helper';
import { resolveGa4TrafficType } from '@infraestructure/helpers/analytics/ga4-internal-traffic.helper';
import { sanitizeGa4Url } from '@infraestructure/helpers/analytics/ga4-url.helper';

type Gtag = (...args: unknown[]) => void;
type Ga4Window = Window & { dataLayer?: unknown[]; gtag?: Gtag } & Record<string, unknown>;
type Ga4State = 'idle' | 'active' | 'disabled';

export interface ScrollMetrics {
  scrollTop: number;
  viewportHeight: number;
  documentHeight: number;
}

export const GA4_SCRIPT_ELEMENT_ID = 'ga4-gtag';
const GA4_SCRIPT_URL = 'https://www.googletagmanager.com/gtag/js?id=';
const SCROLL_THRESHOLD = 0.9;
const SCROLL_PERCENT = 90;
// Events fired before the first page_view (e.g. a component's ngOnInit on the initial load)
export const GA4_MAX_PENDING_EVENTS = 20;

/**
 * GA4 through gtag.js. page_view and scroll are sent manually (Enhanced Measurement's
 * history page views and scrolls must be OFF in the stream); sessions, engagement and
 * outbound clicks stay automatic. Every call is best-effort: an ad blocker or any error
 * must never affect the portal.
 */
@Injectable({ providedIn: 'root' })
export class Ga4AnalyticsService extends AnalyticsGateway {
  private readonly _zone = inject(NgZone);
  private readonly _document = inject(DOCUMENT);
  private readonly _measurementId: string = environment.ga4MeasurementId ?? '';

  private _state: Ga4State = 'idle';
  private _userId: string | null = null;
  private _lastPageLocation: string | null = null;
  private _removeScrollListener: (() => void) | null = null;
  private _scrollFrame = 0;
  private _pendingEvents: Array<[string, AnalyticsEventParams]> = [];

  private get _window(): Ga4Window {
    return this._document.defaultView as unknown as Ga4Window;
  }

  trackPageView(routerUrl: string, pageTitle: string | undefined, member: User | null | undefined): void {
    try {
      if (this._state === 'disabled' || member?.id == null) return;

      const userId = String(member.id);
      if (this._state === 'idle' && !this.activate(member, userId)) {
        // This host/build never tags: queued events can't be sent
        this._pendingEvents = [];
        return;
      }

      // Account switch without a reload: never attribute events to the wrong member
      if (userId !== this._userId) {
        this.reset();
        return;
      }

      const pageLocation = sanitizeGa4Url(routerUrl, this._window.location.origin);
      if (!pageLocation || pageLocation === this._lastPageLocation) return;

      const pageReferrer = this._lastPageLocation ?? sanitizeGa4Url(this._document.referrer);
      this._lastPageLocation = pageLocation;

      const pageParams: AnalyticsEventParams = { page_location: pageLocation };
      if (pageReferrer) pageParams['page_referrer'] = pageReferrer;
      if (pageTitle) pageParams['page_title'] = pageTitle;

      // set: automatic events (clicks, engagement) also report the sanitized page
      this.gtag('set', pageParams);
      this.gtag('event', 'page_view', pageParams);
      this.flushPendingEvents();
      this.startScrollTracking();
    } catch {
      // analytics must never break navigation
    }
  }

  trackEvent(eventName: string, params?: AnalyticsEventParams): void {
    try {
      if (!eventName || this._state === 'disabled') return;
      if (this._state === 'idle') {
        // Sent right after the first page_view, so initial-load events aren't lost
        if (this._pendingEvents.length < GA4_MAX_PENDING_EVENTS) this._pendingEvents.push([eventName, { ...(params ?? {}) }]);
        return;
      }
      this.gtag('event', eventName, params ?? {});
    } catch {
      // best-effort
    }
  }

  reset(): void {
    this._pendingEvents = [];
    // Nothing was sent yet: stay idle so a later sign-in can still activate
    if (this._state !== 'active') return;
    try {
      this.stopScrollTracking();
      // Official GA opt-out flag: stops every hit, automatic ones included
      this._window[`ga-disable-${this._measurementId}`] = true;
    } catch {
      // best-effort
    } finally {
      this._state = 'disabled';
      this._userId = null;
      this._lastPageLocation = null;
    }
  }

  protected loadGtagScript(): void {
    if (this._document.getElementById(GA4_SCRIPT_ELEMENT_ID)) return;
    const script = this._document.createElement('script');
    script.id = GA4_SCRIPT_ELEMENT_ID;
    script.async = true;
    script.src = `${GA4_SCRIPT_URL}${encodeURIComponent(this._measurementId)}`;
    script.onerror = () => {
      // blocked or offline: queued calls are simply never sent
    };
    this._document.head.appendChild(script);
  }

  protected getScrollMetrics(): ScrollMetrics {
    const root = this._document.documentElement;
    return {
      scrollTop: this._window.scrollY ?? root.scrollTop,
      viewportHeight: this._window.innerHeight,
      documentHeight: root.scrollHeight,
    };
  }

  private activate(member: User, userId: string): boolean {
    if (!isGa4AllowedHost(this._window.location.hostname, this._measurementId, environment.production)) return false;

    const win = this._window;
    win.dataLayer = win.dataLayer ?? [];
    if (!win.gtag) {
      // gtag.js expects the Arguments object, not an array
      win.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        win.dataLayer!.push(arguments);
      };
    }

    // Identity goes in config, before the first page_view, so every event carries it
    const config: AnalyticsEventParams = {
      send_page_view: false,
      user_id: userId,
      brand: resolveGa4Brand(win.location.hostname),
    };
    const trafficType = resolveGa4TrafficType(member);
    if (trafficType) config['traffic_type'] = trafficType;

    this.gtag('js', new Date());
    this.gtag('config', this._measurementId, config);
    this.loadGtagScript();

    this._userId = userId;
    this._state = 'active';
    return true;
  }

  private flushPendingEvents(): void {
    const pending = this._pendingEvents;
    this._pendingEvents = [];
    pending.forEach(([eventName, params]) => this.gtag('event', eventName, params));
  }

  private gtag(...args: unknown[]): void {
    this._window.gtag?.(...args);
  }

  private startScrollTracking(): void {
    this.stopScrollTracking();
    this._zone.runOutsideAngular(() => {
      const onScroll = () => {
        if (this._scrollFrame) return;
        this._scrollFrame = this._window.requestAnimationFrame(() => {
          this._scrollFrame = 0;
          this.checkScrollDepth();
        });
      };
      this._window.addEventListener('scroll', onScroll, { passive: true });
      this._removeScrollListener = () => this._window.removeEventListener('scroll', onScroll);
    });
  }

  private stopScrollTracking(): void {
    this._removeScrollListener?.();
    this._removeScrollListener = null;
    if (this._scrollFrame) {
      this._window.cancelAnimationFrame(this._scrollFrame);
      this._scrollFrame = 0;
    }
  }

  /** Fires once per page at 90%; pages that fit the viewport never fire (expected). */
  private checkScrollDepth(): void {
    if (this._state !== 'active') return;
    const { scrollTop, viewportHeight, documentHeight } = this.getScrollMetrics();
    if (documentHeight <= viewportHeight) return;
    if ((scrollTop + viewportHeight) / documentHeight >= SCROLL_THRESHOLD) {
      this.stopScrollTracking();
      this.gtag('event', 'scroll', { percent_scrolled: SCROLL_PERCENT });
    }
  }
}
